import type {
  AgentRuntimeAttachedPullRequest,
  AgentRuntimeActorMetadata,
  AgentRuntimeMessageAttachment,
  AgentRuntimePreferredMcpServerRef,
  AgentRuntimeRequestedSkillRef,
  AgentRuntimeScratchPadRef,
  AgentRuntimeSessionContextRef,
} from "@cord/shared"
import type { SessionActiveWorkState } from "@/lib/session-active-thread-control-state"

export type SandboxStatus =
  | "none"
  | "creating"
  | "starting_services"
  | "starting_agent"
  | "starting_opencode"
  | "connecting_sse"
  | "running"
  | "stopped"
  | "error"

export type QueueDeliveryPhase =
  | "local_only"
  | "server_queued"
  | "server_active"
  | "server_uncertain"

export type QueueBlocker =
  | "transport_offline"
  | "sandbox_starting"
  | "sandbox_resume_needed"
  | "sandbox_error"
  | "transport_not_ready"
  | "branch_rename"
  | "agent_busy"
  | "edit_operation"
  | "pr_adoption"
  | "quick_pr_executing"
  | "submission_uncertain"
  | "unknown"

export type QueueStatusIcon = "sending" | "waiting"

export type QueueStatusVisibility = "hidden" | "immediate" | "deferred"

export type SandboxStartupQueueKind = "start" | "resume" | "recover" | "restore"

export interface PendingQueueRecord {
  id: number | string
  clientMessageId?: string
  clientActionId?: string
  conversationId?: string
  text: string
  attachmentCount?: number
  attachments?: AgentRuntimeMessageAttachment[]
  contextRefs?: AgentRuntimeSessionContextRef[]
  requestedSkillRefs?: AgentRuntimeRequestedSkillRef[]
  preferredMcpServerRefs?: AgentRuntimePreferredMcpServerRef[]
  scratchPadRefs?: AgentRuntimeScratchPadRef[]
  attachedPullRequest?: AgentRuntimeAttachedPullRequest
  actor?: AgentRuntimeActorMetadata
  timestamp: number
  status: "queued" | "dispatching" | "submit_uncertain" | "accepted"
  source: "server" | "local"
  deliveryPhase?: QueueDeliveryPhase
  blocker?: QueueBlocker | null
  queueKind?: "message" | "session_action"
  serverObserved?: boolean
  /**
   * True when a server pending row was merged with this browser's local outbox
   * entry for the same clientMessageId.  This lets render-only handoff code
   * distinguish the user's just-sent optimistic prompt from arbitrary server
   * queued work that should remain trailing.
   */
  localEchoMerged?: boolean
}

export interface LocalOutboxQueueRecord {
  clientMessageId: string
  conversationId?: string
  text: string
  attachmentCount: number
  attachments?: AgentRuntimeMessageAttachment[]
  contextRefs?: AgentRuntimeSessionContextRef[]
  requestedSkillRefs?: AgentRuntimeRequestedSkillRef[]
  preferredMcpServerRefs?: AgentRuntimePreferredMcpServerRef[]
  scratchPadRefs?: AgentRuntimeScratchPadRef[]
  attachedPullRequest?: AgentRuntimeAttachedPullRequest
  timestamp: number
  observedServerStatus: PendingQueueRecord["status"] | null
  renderedHandoffExpiresAt?: number
}

export interface QueuedMessage extends PendingQueueRecord {
  deliveryPhase: QueueDeliveryPhase
  blocker: QueueBlocker | null
  statusText: string
  statusIcon: QueueStatusIcon
  statusVisibility: QueueStatusVisibility
}

export interface QueuePresentationContext {
  sandboxStatus?: SandboxStatus
  bootSource?: string | null
  /**
   * Local/server hint that the current startup is resuming an existing session,
   * even if the boot source has not reached the client yet.
   */
  sandboxResumeInProgress?: boolean
  /** Canonical startup presentation kind when the caller can distinguish it. */
  sandboxStartupKind?: SandboxStartupQueueKind | null
  transportConnected: boolean
  activeEditOperationState?: string | null
  firstDispatchBranchRenameInProgress?: boolean
  transportAuthoritativeReady?: boolean | null
  prAdoptionActive?: boolean
  /** True when a Quick PR operation is actively executing (not queued, not terminal). */
  quickPrExecuting?: boolean
  /** Canonical active-thread work state for the foreground session when available. */
  activeWorkState?: SessionActiveWorkState | null
  /** Whether the session can currently make progress. False suppresses stale running UI. */
  canMakeProgress?: boolean | null
  /**
   * Legacy fallback when canonical foreground work state is unavailable.
   */
  threadActivityState?: "idle" | "running" | "error" | null
}

export function isQueuePresentationContextCurrentResponseActive(
  context: QueuePresentationContext,
): boolean {
  if (context.canMakeProgress === false) {
    return false
  }

  if (context.activeWorkState !== null && context.activeWorkState !== undefined) {
    return (
      context.activeWorkState === "dispatching" ||
      context.activeWorkState === "running" ||
      context.activeWorkState === "aborting"
    )
  }

  return context.threadActivityState === "running"
}

export function isQueuePresentationContextActivelyGenerating(
  context: QueuePresentationContext,
): boolean {
  if (context.canMakeProgress === false) {
    return false
  }

  if (context.activeWorkState !== null && context.activeWorkState !== undefined) {
    return (
      context.activeWorkState === "running" ||
      context.activeWorkState === "aborting"
    )
  }

  return context.threadActivityState === "running"
}

export function isQueuePresentationContextResponseStarting(
  context: QueuePresentationContext,
): boolean {
  if (context.canMakeProgress === false) {
    return false
  }

  if (!context.transportConnected) {
    return false
  }

  if (context.transportAuthoritativeReady === false) {
    return false
  }

  if (context.activeWorkState !== null && context.activeWorkState !== undefined) {
    return (
      context.activeWorkState === "requested" ||
      context.activeWorkState === "dispatching"
    )
  }

  return false
}

export function hasStoredQueuedPresentation(message: {
  queueKind?: PendingQueueRecord["queueKind"]
  serverObserved?: boolean
  status?: string
  statusText?: string
  statusIcon?: QueueStatusIcon
  statusVisibility?: QueueStatusVisibility
}): boolean {
  if (
    typeof message.statusText !== "string" ||
    (message.statusIcon !== "sending" && message.statusIcon !== "waiting") ||
    (message.statusVisibility !== "hidden" &&
      message.statusVisibility !== "immediate" &&
      message.statusVisibility !== "deferred")
  ) {
    return false
  }

  return (
    message.queueKind === "session_action" &&
    message.serverObserved === true &&
    message.status === "submit_uncertain"
  )
}

function isSessionActionPendingRecord(message: PendingQueueRecord): boolean {
  return message.queueKind === "session_action"
}

function isBlockingEditOperationState(value: string | null | undefined): boolean {
  return (
    value === "queued" ||
    value === "aborting" ||
    value === "awaiting_idle" ||
    value === "reverting" ||
    value === "resending"
  )
}

function isResumeBootSource(bootSource: string | null | undefined): boolean {
  return bootSource === "resume"
}

function isRecoveryBootSource(bootSource: string | null | undefined): boolean {
  return bootSource === "snapshot"
}

function getSandboxStartupQueueKind(
  context: QueuePresentationContext,
): SandboxStartupQueueKind {
  if (context.sandboxStartupKind) {
    return context.sandboxStartupKind
  }

  if (context.sandboxResumeInProgress === true || context.sandboxStatus === "stopped") {
    return "resume"
  }

  if (isResumeBootSource(context.bootSource)) {
    return "resume"
  }

  if (isRecoveryBootSource(context.bootSource)) {
    return "recover"
  }

  return "start"
}

function getSandboxStartupQueueStatusText(context: QueuePresentationContext): string {
  const startupKind = getSandboxStartupQueueKind(context)
  if (startupKind === "restore") {
    return "Restoring sandbox from cold storage..."
  }

  if (
    context.sandboxStatus === "starting_agent" ||
    context.sandboxStatus === "starting_opencode"
  ) {
    return "Starting agent..."
  }

  if (context.sandboxStatus === "connecting_sse") {
    return "Connecting..."
  }

  if (startupKind === "resume") {
    return "Resuming sandbox..."
  }

  if (startupKind === "recover") {
    return "Recovering sandbox..."
  }

  if (context.sandboxStatus === "starting_services") {
    return "Starting workspace services..."
  }

  return "Starting sandbox..."
}

// The prefix used by the backend for synthetic agent-mode-switch messages
// (e.g. "agent-mode-switch:build:<dedupeKey>"). These are internal mechanism
// messages and should never appear as visible bubbles in the user-facing queue UI.
export const AGENT_MODE_SWITCH_CLIENT_MESSAGE_ID_PREFIX = "agent-mode-switch"
export const OPENCODE_LENGTH_RECOVERY_CLIENT_MESSAGE_ID_PREFIX = "opencode-length-recovery"

export function isAgentModeSwitchPendingMessage(message: {
  clientMessageId?: string
}): boolean {
  return (
    typeof message.clientMessageId === "string" &&
    message.clientMessageId.startsWith(AGENT_MODE_SWITCH_CLIENT_MESSAGE_ID_PREFIX + ":")
  )
}

export function isOpenCodeLengthRecoveryPendingMessage(message: {
  clientMessageId?: string
}): boolean {
  return (
    typeof message.clientMessageId === "string" &&
    message.clientMessageId.startsWith(OPENCODE_LENGTH_RECOVERY_CLIENT_MESSAGE_ID_PREFIX + ":")
  )
}

export function buildLocalOutboxQueueRecord(input: LocalOutboxQueueRecord): PendingQueueRecord {
  return {
    id: `local:${input.clientMessageId}`,
    clientMessageId: input.clientMessageId,
    clientActionId: undefined,
    conversationId: input.conversationId,
    text: input.text,
    attachmentCount: input.attachmentCount,
    attachments: input.attachments,
    contextRefs: input.contextRefs,
    requestedSkillRefs: input.requestedSkillRefs,
    preferredMcpServerRefs: input.preferredMcpServerRefs,
    scratchPadRefs: input.scratchPadRefs,
    attachedPullRequest: input.attachedPullRequest,
    timestamp: input.timestamp,
    status: input.observedServerStatus ?? "queued",
    source: "local",
    queueKind: "message",
    serverObserved: input.observedServerStatus !== null,
    localEchoMerged: false,
  }
}

// Prefer the furthest-along queue lifecycle state when duplicate server rows
// for the same logical message briefly coexist during replay / resume. This
// mirrors the intended pending-message progression; if the backend adds a new
// intermediate state later, update this list deliberately instead of relying on
// incidental array order.
const PENDING_QUEUE_STATUS_PREFERENCE = [
  "queued",
  "submit_uncertain",
  "accepted",
  "dispatching",
] as const

function getPendingQueueRecordStatusPreferenceIndex(args: {
  status: PendingQueueRecord["status"]
}): number {
  return PENDING_QUEUE_STATUS_PREFERENCE.indexOf(args.status)
}

function getPendingQueueRecordDeduplicationKey(args: {
  message: PendingQueueRecord
}): string | null {
  if (
    args.message.queueKind === "session_action" &&
    typeof args.message.clientActionId === "string" &&
    args.message.clientActionId.length > 0
  ) {
    return `session_action:clientActionId:${args.message.clientActionId}`
  }

  if (
    typeof args.message.clientMessageId === "string" &&
    args.message.clientMessageId.length > 0
  ) {
    return `message:clientMessageId:${args.message.clientMessageId}`
  }

  if (typeof args.message.id === "number") {
    return `${args.message.queueKind ?? "message"}:id:${args.message.id}`
  }

  if (typeof args.message.id === "string" && args.message.id.length > 0) {
    return `${args.message.queueKind ?? "message"}:id:${args.message.id}`
  }

  return null
}

function hasPendingQueueRecordRenderableMetadata(args: {
  message: PendingQueueRecord
}): boolean {
  return Boolean(
    (args.message.attachments?.length ?? 0) > 0 ||
      (typeof args.message.attachmentCount === "number" && args.message.attachmentCount > 0) ||
      (args.message.contextRefs?.length ?? 0) > 0 ||
      (args.message.requestedSkillRefs?.length ?? 0) > 0 ||
      (args.message.preferredMcpServerRefs?.length ?? 0) > 0 ||
      (args.message.scratchPadRefs?.length ?? 0) > 0 ||
      args.message.attachedPullRequest ||
      args.message.actor,
  )
}

function hasConcretePendingQueueRecordId(args: {
  message: PendingQueueRecord
}): boolean {
  return typeof args.message.id === "number"
}

function choosePreferredPendingQueueRecord(args: {
  current: PendingQueueRecord
  duplicate: PendingQueueRecord
}): PendingQueueRecord {
  const currentStatusPreference = getPendingQueueRecordStatusPreferenceIndex({
    status: args.current.status,
  })
  const duplicateStatusPreference = getPendingQueueRecordStatusPreferenceIndex({
    status: args.duplicate.status,
  })

  if (duplicateStatusPreference > currentStatusPreference) {
    return args.duplicate
  }
  if (currentStatusPreference > duplicateStatusPreference) {
    return args.current
  }

  if (args.duplicate.source === "server" && args.current.source !== "server") {
    return args.duplicate
  }
  if (args.current.source === "server" && args.duplicate.source !== "server") {
    return args.current
  }

  const currentHasRenderableMetadata = hasPendingQueueRecordRenderableMetadata({
    message: args.current,
  })
  const duplicateHasRenderableMetadata = hasPendingQueueRecordRenderableMetadata({
    message: args.duplicate,
  })
  if (duplicateHasRenderableMetadata && !currentHasRenderableMetadata) {
    return args.duplicate
  }
  if (currentHasRenderableMetadata && !duplicateHasRenderableMetadata) {
    return args.current
  }

  const currentHasConcreteId = hasConcretePendingQueueRecordId({ message: args.current })
  const duplicateHasConcreteId = hasConcretePendingQueueRecordId({ message: args.duplicate })
  if (duplicateHasConcreteId && !currentHasConcreteId) {
    return args.duplicate
  }
  if (currentHasConcreteId && !duplicateHasConcreteId) {
    return args.current
  }

  return args.current
}

function mergePendingQueueRecordActors(args: {
  primary: PendingQueueRecord["actor"]
  secondary: PendingQueueRecord["actor"]
}): PendingQueueRecord["actor"] {
  if (!args.primary) {
    return args.secondary
  }
  if (!args.secondary) {
    return args.primary
  }

  return {
    ...args.secondary,
    ...args.primary,
  }
}

function choosePendingQueueRecordId(args: {
  primary: PendingQueueRecord
  secondary: PendingQueueRecord
}): PendingQueueRecord["id"] {
  if (typeof args.primary.id === "number") {
    return args.primary.id
  }
  if (typeof args.secondary.id === "number") {
    return args.secondary.id
  }
  return args.primary.id
}

function mergeDuplicatePendingQueueRecord(args: {
  current: PendingQueueRecord
  duplicate: PendingQueueRecord
}): PendingQueueRecord {
  const preferred = choosePreferredPendingQueueRecord(args)
  const fallback = preferred === args.current ? args.duplicate : args.current

  return {
    ...fallback,
    ...preferred,
    id: choosePendingQueueRecordId({ primary: preferred, secondary: fallback }),
    clientMessageId: preferred.clientMessageId ?? fallback.clientMessageId,
    clientActionId: preferred.clientActionId ?? fallback.clientActionId,
    text: preferred.text.length > 0 ? preferred.text : fallback.text,
    attachmentCount: preferred.attachmentCount ?? fallback.attachmentCount,
    attachments: preferred.attachments ?? fallback.attachments,
    contextRefs: preferred.contextRefs ?? fallback.contextRefs,
    requestedSkillRefs: preferred.requestedSkillRefs ?? fallback.requestedSkillRefs,
    preferredMcpServerRefs:
      preferred.preferredMcpServerRefs ?? fallback.preferredMcpServerRefs,
    scratchPadRefs: preferred.scratchPadRefs ?? fallback.scratchPadRefs,
    attachedPullRequest:
      preferred.attachedPullRequest ?? fallback.attachedPullRequest,
    actor: mergePendingQueueRecordActors({
      primary: preferred.actor,
      secondary: fallback.actor,
    }),
    timestamp: Math.min(preferred.timestamp, fallback.timestamp),
    status:
      getPendingQueueRecordStatusPreferenceIndex({ status: preferred.status }) >=
      getPendingQueueRecordStatusPreferenceIndex({ status: fallback.status })
        ? preferred.status
        : fallback.status,
    source:
      preferred.source === "server" || fallback.source === "server"
        ? "server"
        : "local",
    queueKind: preferred.queueKind ?? fallback.queueKind,
    serverObserved:
      preferred.serverObserved === true || fallback.serverObserved === true,
    localEchoMerged:
      preferred.localEchoMerged === true || fallback.localEchoMerged === true,
  }
}

export function dedupePendingQueueRecords(args: {
  messages: PendingQueueRecord[]
}): PendingQueueRecord[] {
  if (args.messages.length < 2) {
    return args.messages
  }

  const deduped: PendingQueueRecord[] = []
  const indexByKey = new Map<string, number>()

  for (const message of args.messages) {
    const deduplicationKey = getPendingQueueRecordDeduplicationKey({ message })
    if (deduplicationKey === null) {
      deduped.push(message)
      continue
    }

    const existingIndex = indexByKey.get(deduplicationKey)
    if (existingIndex === undefined) {
      indexByKey.set(deduplicationKey, deduped.length)
      deduped.push(message)
      continue
    }

    deduped[existingIndex] = mergeDuplicatePendingQueueRecord({
      current: deduped[existingIndex]!,
      duplicate: message,
    })
  }

  return deduped
}

export function mergePendingQueueRecords(input: {
  localOutboxEntries: LocalOutboxQueueRecord[]
  serverPendingMessages: PendingQueueRecord[]
  serverPendingSessionActions?: PendingQueueRecord[]
}): PendingQueueRecord[] {
  const mergedServerPendingMessages = dedupePendingQueueRecords({
    messages: [
      ...input.serverPendingMessages,
      ...(input.serverPendingSessionActions ?? []),
    ],
  })
  const serverByClientMessageId = new Map<string, PendingQueueRecord>()
  for (const message of mergedServerPendingMessages) {
    if (typeof message.clientMessageId === "string" && message.clientMessageId.length > 0) {
      serverByClientMessageId.set(message.clientMessageId, message)
    }
  }

  const merged: PendingQueueRecord[] = mergedServerPendingMessages.map((message) => {
    if (!message.clientMessageId) return message
    const localEntry = input.localOutboxEntries.find((entry) => entry.clientMessageId === message.clientMessageId)
    if (!localEntry) return message
    return {
      ...message,
      attachments: message.attachments ?? localEntry.attachments,
      contextRefs: message.contextRefs ?? localEntry.contextRefs,
      requestedSkillRefs:
        message.requestedSkillRefs ?? localEntry.requestedSkillRefs,
      preferredMcpServerRefs:
        message.preferredMcpServerRefs ?? localEntry.preferredMcpServerRefs,
      scratchPadRefs: message.scratchPadRefs ?? localEntry.scratchPadRefs,
      attachedPullRequest: message.attachedPullRequest ?? localEntry.attachedPullRequest,
      attachmentCount: message.attachmentCount ?? localEntry.attachmentCount,
      localEchoMerged: true,
    }
  })

  for (const entry of input.localOutboxEntries) {
    if (serverByClientMessageId.has(entry.clientMessageId)) continue
    merged.push(buildLocalOutboxQueueRecord(entry))
  }

  return dedupePendingQueueRecords({ messages: merged })
    .sort((left, right) => left.timestamp - right.timestamp)
}

export function deriveQueuedMessagePresentation(
  message: PendingQueueRecord,
  context: QueuePresentationContext,
): Pick<QueuedMessage, "deliveryPhase" | "blocker" | "statusText" | "statusIcon" | "statusVisibility"> {
  const queuedLabel = isSessionActionPendingRecord(message)
    ? "Queued command"
    : "Queued"
  const processingLabel = isSessionActionPendingRecord(message)
    ? "Running command..."
    : "Processing..."
  const uncertainLabel = isSessionActionPendingRecord(message)
    ? "Still confirming this command..."
    : "Still confirming this send..."

  if (message.status === "submit_uncertain") {
    return {
      deliveryPhase: "server_uncertain",
      blocker: "submission_uncertain",
      statusText: uncertainLabel,
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }

  if (isServerActivePendingStatus(message.status)) {
    return {
      deliveryPhase: "server_active",
      blocker: null,
      statusText: processingLabel,
      statusIcon: "waiting",
      statusVisibility: "hidden",
    }
  }

  const deliveryPhase: QueueDeliveryPhase =
    message.source === "local" && message.serverObserved !== true
      ? "local_only"
      : "server_queued"
  const sandboxStartupStatusText = getSandboxStartupQueueStatusText(context)

  if (deliveryPhase === "local_only") {
    if (!context.transportConnected) {
      return {
        deliveryPhase,
        blocker: "transport_offline",
        statusText: "Will send when you're reconnected",
        statusIcon: "waiting",
        statusVisibility: "immediate",
      }
    }

    if (context.sandboxStatus === "none" || context.sandboxStatus === "creating") {
      return {
        deliveryPhase,
        blocker: "sandbox_starting",
        statusText: sandboxStartupStatusText,
        statusIcon: "waiting",
        statusVisibility: "immediate",
      }
    }
    if (context.sandboxStatus === "starting_services") {
      return {
        deliveryPhase,
        blocker: "sandbox_starting",
        statusText: sandboxStartupStatusText,
        statusIcon: "waiting",
        statusVisibility: "immediate",
      }
    }
    if (
      context.sandboxStatus === "starting_agent" ||
      context.sandboxStatus === "starting_opencode"
    ) {
      return {
        deliveryPhase,
        blocker: "sandbox_starting",
        statusText: sandboxStartupStatusText,
        statusIcon: "waiting",
        statusVisibility: "immediate",
      }
    }
    if (context.sandboxStatus === "connecting_sse") {
      return {
        deliveryPhase,
        blocker: "sandbox_starting",
        statusText: sandboxStartupStatusText,
        statusIcon: "waiting",
        statusVisibility: "immediate",
      }
    }
    if (context.sandboxStatus === "stopped") {
      return {
        deliveryPhase,
        blocker: "sandbox_resume_needed",
        statusText: sandboxStartupStatusText,
        statusIcon: "waiting",
        statusVisibility: "immediate",
      }
    }
    if (context.sandboxStatus === "error") {
      return {
        deliveryPhase,
        blocker: "sandbox_error",
        statusText: "Retrying after a workspace error...",
        statusIcon: "waiting",
        statusVisibility: "immediate",
      }
    }

    if (context.transportAuthoritativeReady === false) {
      return {
        deliveryPhase,
        blocker: "transport_not_ready",
        statusText: isSessionActionPendingRecord(message)
          ? "Getting this command ready..."
          : "Getting this message ready...",
        statusIcon: "waiting",
        statusVisibility: "immediate",
      }
    }

    return {
      deliveryPhase,
      blocker: null,
      statusText: isSessionActionPendingRecord(message) ? "Queueing command..." : "Sending...",
      statusIcon: "sending",
      statusVisibility: "hidden",
    }
  }

  if (context.sandboxStatus === "none" || context.sandboxStatus === "creating") {
    return {
      deliveryPhase,
      blocker: "sandbox_starting",
      statusText: sandboxStartupStatusText,
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (context.sandboxStatus === "starting_services") {
    return {
      deliveryPhase,
      blocker: "sandbox_starting",
      statusText: sandboxStartupStatusText,
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (
    context.sandboxStatus === "starting_agent" ||
    context.sandboxStatus === "starting_opencode"
  ) {
    return {
      deliveryPhase,
      blocker: "sandbox_starting",
      statusText: sandboxStartupStatusText,
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (context.sandboxStatus === "connecting_sse") {
    return {
      deliveryPhase,
      blocker: "sandbox_starting",
      statusText: sandboxStartupStatusText,
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (context.sandboxStatus === "stopped") {
    return {
      deliveryPhase,
      blocker: "sandbox_resume_needed",
      statusText: sandboxStartupStatusText,
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (context.sandboxStatus === "error") {
    return {
      deliveryPhase,
      blocker: "sandbox_error",
      statusText: "Retrying after a workspace error...",
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (isBlockingEditOperationState(context.activeEditOperationState)) {
    return {
      deliveryPhase,
      blocker: "edit_operation",
      statusText: "Finishing the earlier edit...",
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (context.prAdoptionActive) {
    return {
      deliveryPhase,
      blocker: "pr_adoption",
      statusText: "Switching branches...",
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (context.quickPrExecuting) {
    return {
      deliveryPhase,
      blocker: "quick_pr_executing",
      statusText: "Will send after the pull request is ready",
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (context.transportAuthoritativeReady === false) {
    return {
      deliveryPhase,
      blocker: "transport_not_ready",
      statusText: isSessionActionPendingRecord(message)
        ? "Getting this command ready..."
        : "Getting this message ready...",
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (context.firstDispatchBranchRenameInProgress) {
    return {
      deliveryPhase,
      blocker: "branch_rename",
      statusText: "Finishing initial branch setup...",
      statusIcon: "waiting",
      statusVisibility: "immediate",
    }
  }
  if (context.sandboxStatus === "running") {
    // Only show "Will send/run after the current response" when the canonical
    // foreground state says there is still an active response in progress. This
    // suppresses stale-running queue copy after suspension/no-progress states.
    const agentIsActivelyResponding = isQueuePresentationContextCurrentResponseActive(context)
    if (agentIsActivelyResponding) {
      return {
        deliveryPhase,
        blocker: "agent_busy",
        statusText: isSessionActionPendingRecord(message)
          ? "Will run after the current response"
          : "Will send after the current response",
        statusIcon: isSessionActionPendingRecord(message) ? "waiting" : "sending",
        statusVisibility: "deferred",
      }
    }

    // Sandbox is running and agent is idle (or state unknown) — the message
    // should be picked up quickly. Use the generic "Queued" label with
    // deferred visibility so it stays invisible during the normal fast path.
    return {
      deliveryPhase,
      blocker: null,
      statusText: isSessionActionPendingRecord(message)
        ? "Queued command"
        : "Queued",
      statusIcon: "sending",
      statusVisibility: "deferred",
    }
  }

  return {
    deliveryPhase,
    blocker: "unknown",
    statusText: queuedLabel,
    statusIcon: "waiting",
    statusVisibility: "deferred",
  }
}

export function buildQueuedMessages(input: {
  localOutboxEntries: LocalOutboxQueueRecord[]
  serverPendingMessages: PendingQueueRecord[]
  serverPendingSessionActions?: PendingQueueRecord[]
  presentation: QueuePresentationContext
}): QueuedMessage[] {
  return mergePendingQueueRecords(input)
    .filter(
      (message) =>
        !isAgentModeSwitchPendingMessage(message) &&
        !isOpenCodeLengthRecoveryPendingMessage(message),
    )
    .map((message) => ({
      ...message,
      ...deriveQueuedMessagePresentation(message, input.presentation),
    }))
}
function isServerActivePendingStatus(
  value: PendingQueueRecord["status"],
): value is "dispatching" | "accepted" {
  return value === "dispatching" || value === "accepted"
}
